import { Drawer } from 'antd';
import React, { useCallback, useMemo } from 'react';
import { useExploreUIState } from 'features/explore/context/use-explore-ui-state';
import { DeprecatedExploreListItem } from 'features/explore/components/explore-side-bar/components/deprecated-explore-list-item/deprecated-explore-list-item';
import { useSetOverlayPOI } from 'features/explore/context/use-explore-ui-state-actions';
import { ExploreDrawerBanner } from 'features/explore/components/explore-side-bar/components/explore-drawer/explore-drawer-banner';
import { DrawerOptions } from 'features/explore/components/explore-side-bar/components/explore-drawer/drawer-options';
import { getReportEntityFlag } from 'core/services/report-entities-service/report-entities-service';
import { useGetEnclosingComplexOptions } from 'features/explore/hooks/use-explore-enclosing-complex';
import { useIsIdSelected } from 'features/explore/hooks/use-explore-selection';
import {
    useGetActiveOverlay,
    useGetOverlayPOI,
} from 'features/explore/hooks/use-explore-overlay-poi';
import { useExplorePermissions } from 'features/explore/hooks/use-explore-permissions';
import { COORDINATES_ENTITY } from 'features/explore/constants';
import { isMarketEntity } from 'utils/market-poi/market-poi';
import { ExploreDrawerFooter } from './explore-drawer-footer/explore-drawer-footer';
import { useExploreFacade } from 'features/explore/model/hooks/use-explore-facade';
import { DrawerPoi } from 'features/explore/model/types';
import { usePOI } from 'features/explore/model/map/use-poi';
import { BasePlaceEntity } from '@placer-ui/types';
import { DeprecatedPOIDetails } from 'features/explore/shared/components/explore-poi-details/explore-poi-details';
import { getFeatureFlags } from 'core/flow-control';
import { useExplore } from 'features/explore/model/hooks/use-explore';
import { ChainReportOption } from 'features/explore/components/explore-side-bar/components/explore-drawer/explore-drawer-option/options/chain-report-option';
import style from './explore-drawer.module.scss';

// TODO: Replace <DeprecatedPOIDetails poi={poiInfo} />
//  with new <ExplorePoiDetails poi={poi} /> when replacing  poiInfo with IPoi

type ExploreDrawerComponentProps = {
    poiInfo: BasePlaceEntity;
    enableSelection?: boolean;
};

export const ExploreDrawerComponent = ({
    poiInfo,
    enableSelection = true,
}: ExploreDrawerComponentProps) => {
    const { customPoiAddresses } = useExploreUIState();
    const getEnclosingComplex = useGetEnclosingComplexOptions();
    const { hasStoreIdBadgeMoreOptionsPermission } = useExplorePermissions();
    const flag = getReportEntityFlag(poiInfo);
    const { accesibility_enable_explore_property_reports_ff } = getFeatureFlags();

    const drawerCurrentOption = useMemo(() => {
        if (isMarketEntity(poiInfo)) return 'markets';

        if (
            poiInfo.category === 'Address' ||
            poiInfo.provider_data?.entity_id === COORDINATES_ENTITY ||
            !!customPoiAddresses[poiInfo.provider_data?.entity_id || poiInfo.id]
        )
            return 'unknown_flag';

        return flag || 'default';
    }, [customPoiAddresses, flag, poiInfo]);

    return (
        <div data-testid={'explore-drawer'} className={style.exploreDrawerComponent}>
            {accesibility_enable_explore_property_reports_ff ? (
                <DeprecatedPOIDetails poi={poiInfo} />
            ) : (
                <DeprecatedExploreListItem
                    poi={poiInfo}
                    hasStoreIdBadgePermission={hasStoreIdBadgeMoreOptionsPermission}
                />
            )}
            {flag && <ExploreDrawerBanner poi={poiInfo} />}
            <div className={style.reportOptionsList}>
                {DrawerOptions[drawerCurrentOption].map(
                    ({ Component: OptionComponent, value }, index) => {
                        const openPropertyForMarketOption =
                            // Second market option is to open the market entity in property report
                            drawerCurrentOption === 'markets' && index === 1;

                        const props = {
                            value,
                            poi: poiInfo,
                            enclosingComplex: getEnclosingComplex(poiInfo.id),
                            enableSelection: openPropertyForMarketOption ? false : enableSelection,
                            enforceOpenPropertyReport: openPropertyForMarketOption,
                            key: `report-option-${value}`,
                        };

                        return <OptionComponent {...props} />;
                    },
                )}
                {accesibility_enable_explore_property_reports_ff && poiInfo.parent_chain_id && (
                    <ChainReportOption poi={poiInfo} />
                )}
            </div>
            <ExploreDrawerFooter poiInfo={poiInfo} />
        </div>
    );
};

type ExploreDrawerProps = {
    drawerPoi: DrawerPoi;
};

export const ExploreDrawer = ({ drawerPoi }: ExploreDrawerProps) => {
    const exploreFacade = useExploreFacade();
    const exploreModel = useExplore();
    const { id, type } = drawerPoi;
    const searchPoi = exploreModel.searchPoiCollection.getItem(id);
    const poi = usePOI(id, type);
    const poiInfo = (searchPoi || poi)?.getPoiData();

    const getActiveOverlay = useGetActiveOverlay();
    const getOverlayPOI = useGetOverlayPOI();

    const setOverlayPOI = useSetOverlayPOI();
    const getIsIdSelected = useIsIdSelected();

    // on dismiss, reset the overlay if the current poi is not selected
    const onClose = useCallback(() => {
        if (poiInfo && getOverlayPOI(poiInfo) && !getIsIdSelected(poiInfo)) {
            setOverlayPOI({
                sourcePoi: poiInfo.id,
                overlayPoi: getActiveOverlay(poiInfo) ?? getOverlayPOI(poiInfo)[0],
            });
        }
        exploreFacade.clearDrawerPoi();
    }, [poiInfo, getActiveOverlay, getIsIdSelected, getOverlayPOI, setOverlayPOI, exploreFacade]);

    if (!poiInfo) {
        return null;
    }

    return (
        <Drawer
            title="Report Options"
            placement="bottom"
            height={'80%'}
            className={style.exploreDrawer}
            closable={true}
            onClose={onClose}
            destroyOnClose={true}
            visible={!!poiInfo}
            getContainer={false}
            data-testid={'explore-options-drawer-container'}
        >
            <ExploreDrawerComponent poiInfo={poiInfo} />
        </Drawer>
    );
};
